import { Maybe, maybe } from '@passionware/monads';
import { Booking, Production, RateInterval, Resource } from '__types__';
import { get } from 'lodash';
import { useSelector } from 'react-redux';
import {
  getResourceSlotFees,
  getResourceSlotRate
} from 'v1/helpers/byModel/ResourceSlotHelper';
import { makeGetExpensesByBookingId } from '../../../../../store/v1/expenses/expenses.selectors.js';
import { getProductionResourcing } from '../../../../../store/v1/productions/productions.selectors';
import { BookingEntity, BookingEntitySpec } from '../types';

export function useBookingSlot(productionId: number, slotId: number) {
  const slotParents = useSelector(state =>
    // @ts-expect-error -- js ts interoperability
    getProductionResourcing(state, { production_id: productionId })
  ).data;

  return slotParents.find(x => x.slot.id === slotId)?.slot;
}

export function useBookingEntity(spec: BookingEntitySpec): BookingEntity {
  // Redux selectors
  const production = useProduction(spec.productionId);
  const bookings = useSelector((state: any) => state.bookings.data);
  const booking = maybe.map(spec.bookingId, bookingId =>
    get(bookings, [bookingId])
  );

  const bookingSlot = useBookingSlot(spec.productionId, spec.slotId);

  // Get expenses for booking
  const getExpensesByBookingId = makeGetExpensesByBookingId();
  const expensesByBookingId = useSelector((state: any) =>
    getExpensesByBookingId(state)
  );

  // Calculate estimate value
  const estimateRate = getResourceSlotRate(bookingSlot);
  const estimateFees = getResourceSlotFees(bookingSlot);
  const estimateValue = {
    rate: {
      currency: estimateRate.currency || production.budget_currency || 'USD',
      amount: estimateRate.amount ?? null,
      interval: estimateRate.interval || RateInterval.Daily,
      quantity: estimateRate.quantity ?? 0
    },
    fees: {
      agency: estimateFees?.agencyFee ?? 0,
      payroll: estimateFees?.payrollFee ?? 0,
      processing: estimateFees?.processingFee ?? 0
    }
  };

  return {
    estimateValue,
    actualValue: maybe.map(booking, booking => {
      // Generate booking expenses from the expenses by booking ID
      const bookingExpenses = booking
        ? expensesByBookingId[booking.id] || []
        : [];

      // Calculate booking rate and fees
      // Calculate actual value
      return {
        rate: {
          currency:
            booking.rate_currency || production.budget_currency || 'USD',
          amount: booking.rate_amount ?? null,
          interval: booking.rate_interval || RateInterval.Daily
        },
        fees: {
          agency: booking.rate_agency_fee,
          payroll: booking.rate_payroll_fee,
          processing: booking.rate_processing_fee
        },
        expenses: bookingExpenses
      };
    })
  };
}

export function useBooking(id: Maybe<Booking['id']>) {
  const booking = useSelector((state: any) =>
    maybe.isPresent(id) ? state.bookings.data[id] : null
  );

  return maybe.of(booking as Booking);
}

export function useResource(id: Maybe<Resource['id']>) {
  const resource = useSelector((state: any) =>
    maybe.mapOrNull(id, id => state.contacts.data[id])
  );

  return maybe.of(resource as Resource);
}

export function useProduction(productionId: Maybe<Production['id']>) {
  const productions = useSelector((state: any) => state.productions.data);
  return maybe.mapOrNull(productionId, id => get(productions, [id]));
}
